<template>
  <v-container class="fill-height">
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="4">
        <v-card>
          <v-card-title class="text-center d-flex flex-column align-center">
            <img src="../assets/logo.png" alt="" width="56" height="56" class="mb-2" />
            Gigdriver
          </v-card-title>
          <v-card-subtitle class="text-center">Sign in or create an account</v-card-subtitle>

          <v-card-text>
            <v-alert v-if="error" type="error" closable @click:close="error = null">
              {{ error }}
            </v-alert>

            <v-tabs v-model="tab">
              <v-tab value="signin">Sign In</v-tab>
              <v-tab value="signup">Sign Up</v-tab>
            </v-tabs>

            <v-window v-model="tab" class="mt-4">
              <!-- Sign In Tab -->
              <v-window-item value="signin">
                <v-form @submit.prevent="handleSignIn">
                  <v-text-field
                    v-model="email"
                    label="Email"
                    type="email"
                    required
                    :disabled="isLoading"
                  ></v-text-field>
                  <v-text-field
                    v-model="password"
                    label="Password"
                    type="password"
                    required
                    :disabled="isLoading"
                  ></v-text-field>
                  <v-btn type="submit" color="primary" block class="mt-4" :loading="isLoading">
                    Sign In
                  </v-btn>
                </v-form>
              </v-window-item>

              <!-- Sign Up Tab -->
              <v-window-item value="signup">
                <v-form @submit.prevent="handleSignUp">
                  <v-text-field
                    v-model="displayName"
                    label="Full Name"
                    required
                    :disabled="isLoading"
                  ></v-text-field>
                  <v-text-field
                    v-model="email"
                    label="Email"
                    type="email"
                    required
                    :disabled="isLoading"
                  ></v-text-field>
                  <v-text-field
                    v-model="password"
                    label="Password"
                    type="password"
                    required
                    :disabled="isLoading"
                  ></v-text-field>
                  <v-text-field
                    v-model="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    required
                    :disabled="isLoading"
                    :rules="[v => v === password || 'Passwords do not match']"
                  ></v-text-field>
                  <v-btn type="submit" color="primary" block class="mt-4" :loading="isLoading">
                    Create Account
                  </v-btn>
                </v-form>
              </v-window-item>
            </v-window>

            <v-divider class="my-4">or</v-divider>

            <v-btn
              color="secondary"
              block
              :loading="isLoading"
              @click="handleGoogleSignIn"
            >
              Sign in with Google
            </v-btn>
          </v-card-text>
        </v-card>

        <p class="text-caption text-center text-medium-emphasis mt-4">
          By continuing you agree to our
          <router-link :to="{ name: 'TermsOfService' }">Terms of Service</router-link>
          and
          <router-link :to="{ name: 'PrivacyPolicy' }">Privacy Policy</router-link>.
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

// Where to go after sign-in/sign-up — e.g. an offer link that bounced through
// /login. Only ever a same-app path, never an absolute/external URL.
function redirectTarget(): string {
  const r = route.query.redirect;
  return typeof r === 'string' && r.startsWith('/') && !r.startsWith('//') ? r : '/';
}

const tab = ref('signin');
const email = ref('');
const password = ref('');
const displayName = ref('');
const confirmPassword = ref('');
const error = ref<string | null>(null);
const isLoading = ref(false);

async function handleSignIn() {
  try {
    isLoading.value = true;
    error.value = null;
    await authStore.login(email.value, password.value);
    router.push(redirectTarget());
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Sign in failed';
  } finally {
    isLoading.value = false;
  }
}

async function handleSignUp() {
  try {
    isLoading.value = true;
    error.value = null;
    await authStore.signup(email.value, password.value, displayName.value);
    router.push(redirectTarget());
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Sign up failed';
  } finally {
    isLoading.value = false;
  }
}

async function handleGoogleSignIn() {
  try {
    isLoading.value = true;
    error.value = null;
    await authStore.loginWithGoogle();
    router.push(redirectTarget());
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Google sign in failed';
  } finally {
    isLoading.value = false;
  }
}
</script>
